import { useEffect, useMemo, useState } from 'react';
import { keyBy, mapValues, pickBy } from 'lodash';

import type { GetExecutionResponse } from '@blockworks/platform/api/research/studio/execution';
import type {
    StudioVisualizationConfig,
    StudioVisualizationType,
} from '@blockworks/platform/api/research/studio/visualizations';

import { getVisualizationParameterMeta } from '../../workflows';

const useActiveControls = ({
    config,
    execution,
    type,
}: {
    config: StudioVisualizationConfig<any>;
    execution: GetExecutionResponse['data'];
    type: StudioVisualizationType;
}) => {
    const { isEnabled, activeControls, initialParams } = useMemo(() => {
        const parameters = getVisualizationParameterMeta({ config, type, execution });
        const meta = keyBy(parameters, 'id');
        const activeControls = pickBy(meta, param => param.active && param.enabled);
        return {
            isEnabled: Object.keys(activeControls).length > 0,
            activeControls: mapValues(activeControls, param => {
                return { ...param, views: param.views.filter(view => !param.excludes.includes(view.value)) };
            }),
            initialParams: mapValues(activeControls, param => param.initialValue!),
        };
    }, [config, execution, type]);

    const [params, setParams] = useState(initialParams);

    /**
     * @TODO Move this into the transformation workflow to avoid unnecessary re-renders
     * Should fully reset component if this changes
     */
    useEffect(() => {
        setParams(mapValues(activeControls, param => param.initialValue!));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Object.values(mapValues(activeControls, param => param.initialValue!)).join('-')]);

    return { isEnabled, activeControls, params, setParams };
};

export { useActiveControls };
